import { createMemo, createRoot } from 'solid-js';
import { daemonStore } from '~/state/daemon';
import { projectsStore } from '~/state/projects';
import { chatStore, unreadCountFor } from '~/state/chat';
import * as kp from '~/lib/known-projects';
import type { RailRowData, RailRowDot } from '~/components/ProjectsRailRow';
import { instanceLinkStatus } from '~/lib/connection-status';
import { livePorts, liveClusters } from './discovery';

/**
 * AX6 — a row's dot.
 *
 * When the cockpit holds an instance for this project, its WebSocket IS
 * the truth: `live` only when the socket is open, amber while it is
 * inside the retry budget, gray once it has given up. Before AX6 every
 * instance was synthesized `live: true` even with `wsState === 'fatal'`,
 * so a dead project looked healthy forever.
 *
 * With no instance we fall back to the last discovery probe — which
 * only runs at mount, on a manual rescan, and (AX6) on the daemon
 * store's 60s health-poll tick, so a background project that dies goes
 * gray within a minute instead of never.
 */
function dotFor(rowKey: string, probeLive: boolean): RailRowDot {
  const status = instanceLinkStatus(rowKey);
  if (!status) return probeLive ? 'live' : 'dead';
  if (status === 'connected') return 'live';
  if (status === 'reconnecting') return 'reconnecting';
  return 'dead';
}

function initialsFor(name: string): string {
  const words = name.replace(/[^A-Za-z0-9\s\-_]/g, ' ').split(/[\s\-_]+/).filter(Boolean);
  let out: string;
  if (words.length >= 3) out = words.slice(0, 3).map((w) => w[0]).join('');
  else if (words.length === 2) out = (words[0]?.[0] ?? '') + (words[0]?.slice(1, 2) ?? '') + (words[1]?.[0] ?? '');
  else out = (words[0] ?? '').slice(0, 3);
  return out.toUpperCase().padEnd(3, '·').slice(0, 3);
}

export const rows = createRoot(() =>
  createMemo<RailRowData[]>(() => {
  const known = projectsStore.state.list;
  // FC-2 (daemon-centralized) — the central server HOME is never a project row.
  // Collect home cluster ids from any connected instance flagged server_home,
  // then drop persisted known-projects entries that match (stale localStorage
  // from before the home was excluded survives a restart otherwise).
  const homeClusterIds = new Set<string>(kp.homeIds()); // durable (offline-safe)
  for (const [, inst] of Object.entries(daemonStore.state.instances)) {
    if ((inst.health as { server_home?: boolean }).server_home && inst.health.cluster_id)
      homeClusterIds.add(inst.health.cluster_id);
  }
  const livePortSet = livePorts();
  const liveById = liveClusters();
  // Read active port + cluster_id from the instances Map keyed by
  // activeId (V85c).
  const activeId = daemonStore.state.activeId;
  const activeInst = activeId ? daemonStore.state.instances[activeId] : null;
  const activePort = activeInst?.health.port ?? null;
  const activeClusterId = activeInst?.health.cluster_id ?? null;
  const newIds = new Set(projectsStore.state.newClusterIds);
  // V86 — pre-compute which ports are "claimed" by an entry with a
  // real cluster_id, so we can suppress orphan rows (entries lacking
  // cluster_id whose port a sibling already owns).
  const claimedPorts = new Set<number>();
  for (const k of known) {
    if (k.cluster_id) {
      const lp = liveById.get(k.cluster_id);
      claimedPorts.add(lp?.port ?? k.port);
    }
  }
  const result: RailRowData[] = [];
  const seenPorts = new Set<number>();
  const seenClusters = new Set<string>();
  for (const k of known) {
    // FC-2 — never render the server home, even if a stale localStorage entry
    // for it survived (it's the global store, not a project).
    if (k.cluster_id && homeClusterIds.has(k.cluster_id)) continue;
    const liveProbe = k.cluster_id ? liveById.get(k.cluster_id) : null;
    const portCandidate = liveProbe?.port ?? k.port;
    // V86 — orphan suppression. A known-projects entry without
    // cluster_id whose port is owned by a cluster-tagged sibling is
    // stale (operator stopped that project, port got reassigned).
    if (!k.cluster_id && claimedPorts.has(portCandidate)) continue;
    // V83 — a row is also live if it IS the currently-bound daemon
    // project (matched by cluster_id when known, else by port).
    const isActiveBinding =
      (k.cluster_id && activeClusterId && k.cluster_id === activeClusterId) ||
      (!k.cluster_id && activePort !== null && portCandidate === activePort);
    const isLive = !!liveProbe || (!k.cluster_id && livePortSet.has(k.port)) || isActiveBinding;
    if (!isLive) {
      // V79o suppression: hide stale rows only when the cluster (or its
      // port, when no cluster_id is known) is occupied by something live.
      if (k.cluster_id && liveById.has(k.cluster_id)) continue;
      if (!k.cluster_id && livePortSet.has(k.port)) continue;
    }
    const port = portCandidate;
    // FC-2 (daemon-centralized) — ONE daemon/port now serves MANY projects, so
    // the unique key is the cluster_id (project), NOT the port. Dedup cluster'd
    // rows by cluster_id (many can share a port); only port-dedup legacy rows
    // that have no cluster_id.
    if (k.cluster_id) {
      if (seenClusters.has(k.cluster_id)) continue;
    } else if (seenPorts.has(port)) {
      continue;
    }
    seenPorts.add(port);
    if (k.cluster_id) seenClusters.add(k.cluster_id);
    const alias = kp.getAlias(k);
    const display = alias || k.cluster_name || k.cluster_id || `:${port}`;
    const rowKey = k.cluster_id ?? `port:${port}`;
    // MP5 — read activity for this cluster. `working` lights the
    // bouncing slug whenever any conv on this cluster is mid-stream
    // (active or inactive). `hasUnread` (2026-09-27) is the amber dot:
    // an agent of this project FINISHED something the operator has not
    // opened yet. It used to be "any WS event since the last visit",
    // which lit every project permanently.
    const activity = chatStore.state.clusterActivity[rowKey];
    const working = !!(activity && activity.workingConvs.length > 0);
    const hasUnread = unreadCountFor(rowKey) > 0;
    // V107.4 — `architectActive` reflects "Run All in progress on this
    // cluster" — a non-archived roadmap-architect conv exists, even
    // between turns. Drives a soft pulse on the working bar so the
    // operator can see "I'm running" from the rail without opening
    // the chat. Only computable for the active cluster (chatStore
    // exposes the active slice's convMeta); inactive clusters always
    // read false here. Reuses `isActiveBinding` from above.
    const architectActive = !!(isActiveBinding && chatStore.findActiveArchitectConv());
    const dot = dotFor(rowKey, isLive);
    result.push({
      key: rowKey,
      port, base: liveProbe?.base ?? k.base,
      cluster_id: k.cluster_id ?? null,
      cluster_name: k.cluster_name ?? null,
      display, initials: initialsFor(display),
      // A row with a dead socket is a stopped row — `is-stopped` styling
      // stays the base state and the dot refines it.
      live: dot !== 'dead',
      dot,
      working,
      hasUnread,
      architectActive,
      isNew: newIds.has(rowKey),
    });
  }
  // V107.15 — Defensive synthesis for ANY live instance the kp.list()
  // forgot (port-collision sweep race during self-update — see
  // known-projects.ts:175-186). Initial V107.15 only synthesized for
  // state.activeId; field report 2026-05-31 showed inactive clusters
  // can disappear too (operator on Ikamiro, MeshKore Core vanished).
  // Belt-and-braces: walk every entry in daemonStore.state.instances
  // and synthesize a row if it isn't already in `result`.
  const emittedKeys = new Set(result.map((r) => r.key));
  const emittedClusters = new Set(result.map((r) => r.cluster_id).filter((c): c is string => !!c));
  for (const [, inst] of Object.entries(daemonStore.state.instances)) {
    // FC-2 (daemon-centralized) — never synthesize a row for the server HOME
    // (central store, not a project). The boot connection attaches it, but it
    // must not appear in the rail.
    if ((inst.health as { server_home?: boolean }).server_home) continue;
    const cid = inst.health.cluster_id ?? null;
    const port = inst.health.port;
    const synthKey = cid ?? `port:${port}`;
    if (emittedKeys.has(synthKey)) continue;
    if (cid && emittedClusters.has(cid)) continue;
    const display = cid || inst.health.cluster_name || `:${port}`;
    const isThisActive =
      (cid && cid === activeClusterId) ||
      (!cid && activePort !== null && port === activePort);
    // AX6 — was a hardcoded `live: true`, which kept a fatal-socket
    // project looking connected. The instance's own wsState decides.
    const synthDot = dotFor(synthKey, true);
    result.push({
      key: synthKey,
      port,
      base: inst.client.transport.httpBase,
      cluster_id: cid,
      cluster_name: inst.health.cluster_name ?? null,
      display,
      initials: initialsFor(display),
      live: synthDot !== 'dead',
      dot: synthDot,
      working: false,
      hasUnread: false,
      architectActive: !!(isThisActive && chatStore.findActiveArchitectConv()),
      isNew: false,
    });
  }

  // 2026-09-27 — a project the operator just forgot never gets a row,
  // whichever source still lists it (a lagging /projects table, a live
  // instance, a stale discovery probe). The tombstone is the operator's
  // decision; every other signal is the system catching up.
  const visible = result.filter((r) => !(r.cluster_id && kp.isDeleted(r.cluster_id)));

  // CN9 — NO re-sort here. `known` already arrives in stable creation
  // order (known-projects.list()); sorting by port (or anything derived
  // from live probes) re-shuffled siblings sharing one FC-2 port on every
  // bind/poll and made the clicked row jump. Creation order is the rail
  // order; operator-saved order (order.ts) applies on top in ProjectsRail.
  return visible;
  }),
);
